import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { auditService, IAuditStudentWalletHistoryItem, IAuditWalletHistoryFilter, TStudentWalletHistoryType, WalletHistoryList } from "../../../../entities/audit";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { MyError } from "../../../../shared/lib/error/MyError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { useAppSelector } from "../../../../app/store/store";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import { StudentDetailLayout } from "../detail/StudentDetailLayout";
import classes from "./studentWalletHistoryWidget.module.scss";

export const StudentWalletHistoryWidget: FC = () => {
    const {my} = useAppSelector(state => state.myReducer)
    const {id} = useParams<{id: string}>()
    const studentId = id && !Number.isNaN(Number(id)) ? Number(id) : 0
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const [isLoading, setIsLoading] = useState(true)
    const [isFiltersLoading, setIsFiltersLoading] = useState(true)
    const [error, setError] = useState('')
    const [filtersError, setFiltersError] = useState('')
    const [items, setItems] = useState<IAuditStudentWalletHistoryItem[]>([])
    const [filters, setFilters] = useState<IAuditWalletHistoryFilter[]>([])
    const [selectedTypes, setSelectedTypes] = useState<TStudentWalletHistoryType[]>([])
    const canReadHistory = Boolean(studentId) && hasFeature(my, FEATURES.AUDIT.SCHEDULE_HISTORY_READ, studentId)

    useEffect(() => {
        if(!canReadHistory){
            setFilters([])
            setFiltersError('')
            setIsFiltersLoading(false)
            return
        }

        let isActual = true

        const loadFilters = async () => {
            try{
                setIsFiltersLoading(true)
                setFiltersError('')
                const response = await auditService.listWalletHistoryFilters()
                if(isActual){
                    setFilters(response)
                }
            }
            catch(error){
                if(!isActual) return

                const message = error instanceof AuthError || error instanceof MyError
                    ? error.message
                    : 'Ошибка при получении фильтров истории кошелька'

                if(error instanceof AuthError && error.status === 401){
                    setIsAuth(false)
                }

                setFilters([])
                setFiltersError(message)
            }
            finally{
                if(isActual){
                    setIsFiltersLoading(false)
                }
            }
        }

        loadFilters()

        return () => {
            isActual = false
        }
    }, [canReadHistory, setIsAuth])

    useEffect(() => {
        if(!canReadHistory){
            setItems([])
            setIsLoading(false)
            setError('')
            return
        }

        let isActual = true

        const load = async () => {
            try{
                setIsLoading(true)
                setError('')
                const response = await auditService.listStudentWalletHistory(studentId, selectedTypes)
                if(isActual){
                    setItems(response)
                }
            }
            catch(error){
                if(!isActual) return

                if(error instanceof AuthError){
                    setIsAuth(false)
                    setError(error.message)
                    setGlobalMessage({message: error.message, type: 'error'})
                    return
                }

                if(error instanceof MyError){
                    setError(error.message)
                    setGlobalMessage({message: error.message, type: 'error'})
                    return
                }

                setError('Ошибка при получении истории изменения кошелька')
                setGlobalMessage({message: 'Ошибка при получении истории изменения кошелька', type: 'error'})
            }
            finally{
                if(isActual){
                    setIsLoading(false)
                }
            }
        }

        if(studentId){
            load()
        }

        return () => {
            isActual = false
        }
    }, [canReadHistory, selectedTypes, setGlobalMessage, setIsAuth, studentId])

    const onSelectedType = (item: {id: TStudentWalletHistoryType; name: string}) => {
        return (selected: boolean) => {
            setSelectedTypes(current => selected
                ? current.includes(item.id) ? current : [...current, item.id]
                : current.filter(type => type !== item.id)
            )
        }
    }

    const filterItems = filters.map(filter => ({
        id: filter.type,
        name: filter.label,
    }))

    const typeLabels = filters.reduce<Record<string, string>>((acc, filter) => {
        acc[filter.type] = filter.label
        return acc
    }, {})

    if(!studentId){
        return null
    }

    return (
        <StudentDetailLayout
            studentId={studentId}
            selectedTab={3}
            showHistoryTab={canReadHistory}
            showWalletHistoryTab={canReadHistory}
        >
            {canReadHistory
                ? <section className={idmClasses.panel}>
                    <section className={classes.filterPanel}>
                        <section className={classes.filterField}>
                            <span className={classes.filterLabel}>Тип операции</span>
                            <DropDownListSelected<TStudentWalletHistoryType>
                                isLoading={isFiltersLoading}
                                selectedIdItems={selectedTypes}
                                items={filterItems}
                                onSelected={onSelectedType}
                                selectedCount
                                placeholder="Все типы"
                                search
                                searchPlaceholder="Поиск по типам"
                                emptyText="Фильтры не найдены"
                            />
                        </section>
                        <button
                            type="button"
                            className={classes.allButton}
                            onClick={() => setSelectedTypes([])}
                            disabled={!selectedTypes.length}
                        >
                            Все
                        </button>
                    </section>
                    {filtersError && <section className={classes.filtersError}>*{filtersError}</section>}
                    {isLoading
                        ? <section className={idmClasses.loader}><LoaderSpinner /></section>
                        : error
                            ? <section className={idmClasses.placeholder}>{error}</section>
                            : <WalletHistoryList
                                items={items}
                                typeLabels={typeLabels}
                                emptyText="История изменения кошелька пока пуста"
                            />
                    }
                </section>
                : <section className={idmClasses.panel}>
                    <section className={idmClasses.placeholder}>История изменения кошелька недоступна</section>
                </section>
            }
        </StudentDetailLayout>
    )
}
